import { useEffect, useReducer, useContext, useMemo, useState } from "react";
import { StoreContext } from "@providers";
import { Button, Form, Card, message } from "antd";
import { CustomFormDescriptions, Modal } from "@library";
import { FieldsType, InputType } from "@types";
import { useNavigate, useParams } from "react-router-dom";
import { SurveyDto } from "@api";
import { Get as GetSurvey } from "@components/Surveys/SurveyAPI";
import { SurveyContent, SurveyPendingRestoreAlert } from "@components/Surveys";
import { Create } from "@components/MemberSurveys/MemberSurveyAPI";
import { showError } from "@actions";
import {
  buildSurveyWebSpaCreateScopeKey,
  clearSurveyDraftSession,
  getPendingForScope,
  persistFailedSurveySubmission,
  removePendingForScopeKey,
  SurveyFailedSubmissionRecord,
  surveyAnswerMergedFromBackup,
} from "@/utils/surveyOfflinePersistence";

enum ActionKind {
  isLoading,
  pageChange,
  params,
  initPage,
  fetchData,
  survey,
  surveyAnswer,
  isVerified,
  isCompleted,
}

interface IState {
  isLoading: boolean;
  survey?: SurveyDto;
  surveyAnswer?: string;
  isVerified: boolean;
  isCompleted: boolean;
}

type IReducerAction = {
  type: ActionKind;
  payload?: any;
};

const SURVEY_WEB_SPA_CONTEXT = "SurveyWebSPA.MemberSurvey.Create";

const CreatePage = () => {
  const { setBreadcrumb, currentMember } = useContext(StoreContext);
  const params = useParams();
  const navigate = useNavigate();

  const surveyId = params.id ?? "";
  const memberId = currentMember.id ?? "";
  const [pendingNonce, setPendingNonce] = useState(0);
  const [questionnaireCompleteClicked, setQuestionnaireCompleteClicked] =
    useState(false);

  const scopeKey = useMemo(() => {
    if (!memberId || !surveyId) return "";
    return buildSurveyWebSpaCreateScopeKey(memberId, surveyId);
  }, [memberId, surveyId]);

  const pendingRecord = useMemo(() => {
    if (!scopeKey) return null;
    return getPendingForScope(scopeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, pendingNonce]);

  const [form] = Form.useForm(undefined);
  const initialState: IState = {
    isLoading: false,
    isVerified: true,
    isCompleted: false,
  };

  const fields: FieldsType[] = [
    {
      label: "Password",
      name: "password",
      inputType: InputType.Password,
      span: 3,
      formItemProps: {
        rules: [{ required: true }],
      },
    },
  ];

  const reducer = (
    state: IState,
    { type, payload }: IReducerAction
  ): IState => {
    switch (type) {
      case ActionKind.isLoading:
        return {
          ...state,
          isLoading: payload,
        };

      case ActionKind.survey:
        return {
          ...state,
          survey: payload,
          isVerified: !payload.password,
        };
      case ActionKind.surveyAnswer:
        return {
          ...state,
          surveyAnswer: payload,
        };
      case ActionKind.isVerified:
        return {
          ...state,
          isVerified: payload,
        };
      case ActionKind.isCompleted:
        return {
          ...state,
          isCompleted: payload,
        };

      default:
        return state;
    }
  };

  const [
    { isLoading, survey, surveyAnswer, isVerified, isCompleted },
    dispatch,
  ] = useReducer(reducer, initialState);

  const draftStorageKey = useMemo(() => {
    if (!isVerified || isCompleted || !memberId || !survey?.id) return undefined;
    return `ss-draft|${memberId}|${String(survey.id)}`;
  }, [isVerified, isCompleted, memberId, survey?.id]);

  const fetchSurvey = async (id: string) => {
    const data = await GetSurvey(id);
    dispatch({ type: ActionKind.survey, payload: data });
    setBreadcrumb(data.formName);
  };

  const applyBackupRecord = async (p: SurveyFailedSubmissionRecord) => {
    setQuestionnaireCompleteClicked(false);
    if (
      p.payload.memberInfoId != null &&
      String(p.payload.memberInfoId) !== String(memberId)
    ) {
      message.error("This backup is for a different member.");
      return;
    }
    const backupSurveyId = p.surveyId ?? p.payload.surveyId;
    if (
      backupSurveyId != null &&
      surveyId &&
      String(backupSurveyId) !== String(surveyId)
    ) {
      message.error("This backup is for a different questionnaire.");
      return;
    }
    if (!p.surveyId && !survey) {
      message.warning("Backup has no survey reference.");
      return;
    }
    try {
      const sid = String(p.surveyId ?? survey?.id ?? surveyId);
      const s =
        survey && String(survey.id) === sid
          ? survey
          : await GetSurvey(sid);
      dispatch({ type: ActionKind.survey, payload: s });

      const mergedPayload = surveyAnswerMergedFromBackup(
        surveyAnswer,
        p.payload.resultJson,
        draftStorageKey
      );
      clearSurveyDraftSession(`ss-draft|${memberId}|${sid}`);
      dispatch({
        type: ActionKind.surveyAnswer,
        payload: mergedPayload,
      });
      if (p.scopeKey === scopeKey) {
        removePendingForScopeKey(scopeKey);
      }
      setPendingNonce((n) => n + 1);
      message.success("Restored from backup.");
    } catch (e) {
      showError(e);
    }
  };

  const handleRestoreLocalBackup = () => {
    setQuestionnaireCompleteClicked(false);
    const p = scopeKey ? getPendingForScope(scopeKey) : null;
    if (p) void applyBackupRecord(p);
  };

  const onFinish = async (values: any) => {
    try {
      dispatch({ type: ActionKind.isLoading, payload: true });
      if (isVerified) {
        const currentTime = Date.now();

        const lastSubmitTime = localStorage.getItem(currentMember.id!);
        if (
          lastSubmitTime &&
          currentTime - Number(lastSubmitTime) < 5 * 60 * 1000
        ) {
          message.warning(
            "You have already submitted the questionnaire. Please try again in five minutes."
          );
          return;
        }

        try {
          await Create({
            ...values,
            memberInfoId: currentMember.id,
            surveyId: survey?.id,
            resultJson: surveyAnswer,
          });
          localStorage.setItem(currentMember.id!, String(currentTime));

          dispatch({ type: ActionKind.isCompleted, payload: true });
          if (draftStorageKey) clearSurveyDraftSession(draftStorageKey);
          if (scopeKey) removePendingForScopeKey(scopeKey);
        } catch {
          if (scopeKey) {
            persistFailedSurveySubmission({
              scopeKey,
              context: SURVEY_WEB_SPA_CONTEXT,
              payload: {
                ...values,
                memberInfoId: currentMember.id,
                surveyId: survey?.id,
                resultJson: surveyAnswer,
              },
              surveyId: survey?.id ? String(survey.id) : undefined,
            });
            setPendingNonce((n) => n + 1);
          }
        }
      } else {
        if (survey?.password === values.password) {
          dispatch({ type: ActionKind.isVerified, payload: true });
        } else {
          message.warning(
            "Password error! Please contact the relevant staff to obtain the correct password."
          );
        }
      }
    } finally {
      dispatch({ type: ActionKind.isLoading, payload: false });
    }
  };

  useEffect(() => {
    setQuestionnaireCompleteClicked(false);
  }, [surveyId]);

  useEffect(() => {
    const init = async (id: string) => {
      await fetchSurvey(id);
    };
    if (surveyId) {
      init(surveyId);
    }
  }, [surveyId]);

  return (
    <Card
      loading={isLoading}
      extra={
        <Button
          onClick={() => {
            navigate("/");
          }}
        >
          Cancel
        </Button>
      }
    >
      <Form form={form} onFinish={onFinish} autoComplete="off">
        {!isVerified && (
          <Modal
            isNarrow
            title="Verify Password"
            open={!isVerified}
            onOk={() => form.submit()}
            onCancel={() => navigate("/")}
          >
            <CustomFormDescriptions
              isEditing={true}
              fields={fields}
              getFieldValue={form.getFieldValue}
            />
          </Modal>
        )}
        {survey && scopeKey && (
          <SurveyPendingRestoreAlert
            pendingRecord={pendingRecord}
            onRestore={handleRestoreLocalBackup}
            onDismissed={() => setPendingNonce((n) => n + 1)}
            expectedContext={SURVEY_WEB_SPA_CONTEXT}
            onRestoreFromFile={applyBackupRecord}
            showFileUpload={false}
          />
        )}
        {survey && (
          <SurveyContent
            mode={isVerified ? "edit" : "display"}
            json={survey!.content!}
            data={surveyAnswer}
            showCompletedPage={isCompleted}
            draftStorageKey={draftStorageKey}
            backupFileUpload={
              questionnaireCompleteClicked
                ? undefined
                : {
                    expectedContext: SURVEY_WEB_SPA_CONTEXT,
                    onRestoreFromFile: applyBackupRecord,
                  }
            }
            onComplete={(value) => {
              setQuestionnaireCompleteClicked(true);
              dispatch({
                type: ActionKind.surveyAnswer,
                payload: JSON.stringify(value.valuesHash),
              });
              form.submit();
            }}
          />
        )}
      </Form>
    </Card>
  );
};
export default CreatePage;
